import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

type Radek = {
  id: number;
  sku: string;
  product_code: string;
  product_name: string;
  latka: string;
  image: string;
  variant_name: string | null;
  level: number;
  active: number;
  created_at: number;
  created_by: string;
  ended_at: number | null;
  ended_by: string | null;
};

/** Unix ms → „6.8. 00:14". */
function fmtKdy(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getDate()}.${d.getMonth() + 1}. ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Log šití na zakázku — co se kdy začalo nabízet a co to zase vyplo.
 * Vlastní stránka, protože záznamů rychle přibývá.
 */
export function NaZakazkuLogPage() {
  const [rows, setRows] = useState<Radek[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    apiFetch<{ rows: Radek[] }>('/api/vyroba/na-zakazku/log')
      .then((d) => setRows(d.rows))
      .catch((e: unknown) => {
        setRows([]);
        setErr(e instanceof ApiError ? e.describe() : 'Načtení logu selhalo');
      });
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows ?? [];
    return (rows ?? []).filter((r) =>
      `${r.sku} ${r.product_name} ${r.latka} ${r.created_by} ${r.ended_by ?? ''}`
        .toLowerCase()
        .includes(s),
    );
  }, [rows, q]);

  const bezi = (rows ?? []).filter((r) => r.active).length;

  return (
    <>
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Log šití na zakázku</h1>
          <p className="text-sm text-text-secondary mt-0.5">
            Co se kdy začalo nabízet a co to zase vyplo. Právě se nabízí{' '}
            <strong className="text-text">{bezi}</strong> velikostí.
          </p>
        </div>
        <Link
          to="/vyroba/striharna/na-zakazku"
          className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors shrink-0"
        >
          Zpět na přehled
        </Link>
      </div>

      {err && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {err}
        </div>
      )}

      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Hledat podle produktu, SKU, látky nebo toho, kdo přepínal…"
        className="w-full max-w-xl mb-4 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm text-text focus:outline-none focus:border-primary"
      />

      <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
              <th className="px-4 py-2.5 w-14" />
              <th className="px-4 py-2.5">Produkt</th>
              <th className="px-4 py-2.5 w-24">Velikost</th>
              <th className="px-4 py-2.5">Látka</th>
              <th className="px-4 py-2.5 w-24">Slib</th>
              <th className="px-4 py-2.5 w-44">Zapnuto</th>
              <th className="px-4 py-2.5 w-52">Vypnuto</th>
              <th className="px-4 py-2.5 w-28">Stav</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-text-muted">
                  Načítám…
                </td>
              </tr>
            )}
            {rows !== null && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-text-muted">
                  {q.trim() ? 'Nic neodpovídá hledání.' : 'Zatím se nic nezapínalo.'}
                </td>
              </tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-border-light">
                <td className="px-4 py-2">
                  {r.image ? (
                    <img
                      src={r.image}
                      alt=""
                      loading="lazy"
                      className="w-10 h-10 object-contain rounded border border-border bg-white"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded border border-border bg-bg" />
                  )}
                </td>
                <td className="px-4 py-2">
                  <span className="block font-semibold text-text">{r.product_name}</span>
                  <span className="block text-xs font-mono text-text-muted">{r.sku}</span>
                </td>
                <td className="px-4 py-2 text-text">{r.variant_name ?? '—'}</td>
                <td className="px-4 py-2 text-text-secondary">{r.latka || '—'}</td>
                <td className="px-4 py-2 text-text-secondary whitespace-nowrap">
                  do {r.level} dní
                </td>
                <td className="px-4 py-2 text-text-secondary whitespace-nowrap">
                  {fmtKdy(r.created_at)}
                  <span className="block text-xs text-text-muted">{r.created_by}</span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {r.active ? (
                    <span className="text-text-muted">zatím ne</span>
                  ) : (
                    <span className="text-text-secondary">
                      {r.ended_at ? fmtKdy(r.ended_at) : '—'}
                      <span className="block text-xs text-text-muted">{r.ended_by ?? ''}</span>
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {r.active ? (
                    <span className="inline-block rounded-full bg-action-light px-2.5 py-0.5 text-xs font-bold text-action-dark">
                      Nabízí se
                    </span>
                  ) : (
                    <span className="inline-block rounded-full bg-bg px-2.5 py-0.5 text-xs font-semibold text-text-muted">
                      Ukončeno
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
